import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {Subscription} from "rxjs";
import {ContactsService} from "./contacts.service";
import {Person} from "../interfaces/person";
import {MatTableDataSource} from "@angular/material/table";
import Sponsor from "../interfaces/sponsor";
import Organization from "../interfaces/organization";
import {DataFormatService} from "../services/data-format.service";
import {PageEvent} from "@angular/material/paginator";

export enum ContactCategory {
  PEOPLE = 'people',
  ORGANIZATIONS = 'organizations',
  SPONSORS = 'sponsors'
}

type PeopleContact = {
  name: string,
  email: string,
  phone: string,
  organization: string,
  orgUrl: string | null,
  url: string,
  tags: string[]
}

interface PeopleContactTable {
  name: string,
  email: string,
  phone: string,
  organization: string,
  orgUrl: string | null,
  url: string,
  tags: string[]
}

type OrgContact = {
  name: string,
  state: string,
  phone: string,
  website: string,
  url: string,
  tags: string[]
}

interface OrganizationContactTable {
  name: string,
  state: string,
  phone: string,
  website: string,
  url: string,
  tags: string[]
}

type SponsorContact = {
  name: string,
  url: string
}
interface SponsorContactTable {
  name: string,
  url: string
}
@Component({
  selector: 'app-contacts-list',
  templateUrl: './contacts-list.component.html',
  styleUrls: ['./contacts-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContactsListComponent implements OnInit {

  public category:ContactCategory = ContactCategory.PEOPLE

  private routeSubscription: Subscription
  private peopleSubscription: Subscription
  private orgSubscription: Subscription
  private sponsorSubscription: Subscription
  public peopleContacts: PeopleContact[] = []
  public orgContacts: OrgContact[] = []
  public sponsorContacts: SponsorContact[] = []

  public peopleColumns:string[] = ['name', 'email', 'phone', 'organization', 'tags']
  public orgColumns:string[] = ['name', 'state', 'phone', 'website', 'tags']
  public sponsorColumns:string[] = ['name']
  public columns:string[] = []
  public peopleDataSource:MatTableDataSource<PeopleContactTable>
  public sponsorDataSource:MatTableDataSource<SponsorContactTable>
  public orgDataSource:MatTableDataSource<OrganizationContactTable>
  public paginatorSubscription: Subscription
  public total: 0
  public perPage: 0
  public currentLetter: string = 'A'
  constructor(private route: ActivatedRoute, private contactsService: ContactsService, public dataFormat: DataFormatService) {}

  ngOnInit(): void {
    this.route.data.subscribe((data:any) => {
      this.category = data.tab
    })
    this.contactsService.getContacts(new PageEvent(), this.category, this.currentLetter)
    this.peopleSubscription = this.contactsService.people$.subscribe(result => {
      this.peopleContacts = this._setPeopleContacts(result)
      this.peopleDataSource = new MatTableDataSource(this.peopleContacts)
    })

    this.orgSubscription = this.contactsService.organizations$.subscribe(result => {
      this.orgContacts = this._setOrganizationsContacts(result)
      this.orgDataSource = new MatTableDataSource(this.orgContacts)
    })

    this.sponsorSubscription = this.contactsService.sponsors$.subscribe(result => {
      this.sponsorContacts = this._setSponsorsContacts(result);
      this.sponsorDataSource = new MatTableDataSource(this.sponsorContacts)
    })
    this.paginatorSubscription = this.contactsService.listPageData$.subscribe(result => {
      this.total = result.total;
      this.perPage = result.perPage;
    })
    this.columns = this._getTableColumnsByCategory(this.category)
  }

  private _getTableColumnsByCategory(category: ContactCategory){
    switch (category) {
      case ContactCategory.PEOPLE:
        return this.peopleColumns
      case ContactCategory.ORGANIZATIONS:
        return this.orgColumns
      case ContactCategory.SPONSORS:
        return this.sponsorColumns
    }
  }

  public getDataSourceByCategory(category: ContactCategory){
    switch (category) {
      case ContactCategory.PEOPLE:
        return this.peopleDataSource
      case ContactCategory.SPONSORS:
        return this.sponsorDataSource
      case ContactCategory.ORGANIZATIONS:
        return this.orgDataSource
    }
  }

  private _setPeopleContacts(people: Person[]){
    let formattedContacts:PeopleContact[] = []
    if(people){
      people.forEach((person:Person) => {
        let contactTags:string[] = []
        person.entity.entity_types.forEach((tp:any) => {
          contactTags.push(tp.type.name)
        })
        let contact:PeopleContact = {
          name: person.name,
          email: person.entity.emailAddresses.length==0 ? "" : person.entity.emailAddresses[0].emailaddress,
          phone: person.entity.phoneNumbers.length == 0 ? "" : this.dataFormat.phoneFormat(person.entity.phoneNumbers[0].area_code, person.entity.phoneNumbers[0].phone_number),
          organization: person.employees ? person.employees[0]?.organization.name : null,
          orgUrl: person.employees && person.employees.length > 0 ? this.dataFormat.formatLink(person.employees[0]?.organization) : null,
          url: this.dataFormat.formatLink(person),
          tags: contactTags
        }
        formattedContacts.push(contact)
      })
    }
    return formattedContacts
  }

  private _setOrganizationsContacts(orgs: Organization[]){
    let formattedContacts: OrgContact[] = []
    if(orgs){
      orgs.forEach((org:Organization) => {
        let contactTags:string[] = []
        if (org.entity!==undefined){
          org.entity?.entity_types?.forEach((tp:any) => contactTags.push(tp.type.name))
        }
        let phone = ""
        if(org.entity!==undefined && org.entity.phoneNumbers!==undefined && org.entity.phoneNumbers.length){
          phone = this.dataFormat.phoneFormat(org.entity.phoneNumbers[0].area_code, org.entity.phoneNumbers[0].phone_number)
        }
        let state = ""
        if(org.entity!=undefined && org.entity.entityAddresses!==undefined && org.entity.entityAddresses.length){
          state = org.entity.entityAddresses[0].address.zipCode.state.abbreviation
        }
        let webaddress = ""
        if(org.entity!==undefined && org.entity.webAddresses!==undefined && org.entity.webAddresses.length){
          webaddress = this.dataFormat.formatWeblink(org.entity.webAddresses[0])
        }
        let contact:OrgContact = {
          name: org.name,
          phone: phone,
          state: state,
          website: webaddress,
          url: this.dataFormat.formatLink(org),
          tags: contactTags
        }
        formattedContacts.push(contact)
      })
    }
    return formattedContacts
  }

  private _setSponsorsContacts(sponsors: Sponsor[]){
    let formattedContacts: SponsorContact[] = []
    if(sponsors){
      sponsors.forEach((sponsor:Sponsor) => {
        let contact:SponsorContact = {
          name: sponsor.name,
          url: this.dataFormat.formatLink(sponsor)
        }
        formattedContacts.push(contact)
      })
    }
    return formattedContacts
  }

  public getPage(event: PageEvent, letter: string, category: ContactCategory){
    this.currentLetter = letter
    if(!event.pageIndex){
      event.pageIndex = 1
    } else {
      event.pageIndex += 1
    }
    this.contactsService.getPage(event, letter, category)
  }


  ngOnDestroy(){
    this.peopleSubscription.unsubscribe()
    this.orgSubscription.unsubscribe()
    this.sponsorSubscription.unsubscribe()
    this.paginatorSubscription.unsubscribe()
  }

  protected readonly ContactCategory = ContactCategory;
}
